import { useCallback, useState } from 'react';

import { PendingCardTokenActionResult, PendingCardStatus, PendingCardTokenItem } from '@/domain/digital-wallet';

import useInvalidateApplePay from '@domain/digital-wallet/hooks/query/use-invalidate-apple-pay';
import { useActivatePendingCard } from '@domain/digital-wallet/hooks/query/use-pending-cards';
import useDebounce from '@hooks/useDebounce/useDebounce';

import { useActivatePendingCreditCard } from '../../queries/useActivatePendingCreditCard';

export const useActivateDigitalWalletPendingCard = (enabled = false, shouldInvalidateOnSuccess = true) => {
    const invalidateTokens = useInvalidateApplePay();

    const debouncedInvalidateFunction = useDebounce(invalidateTokens, { delay: 2000 });

    const { mutateAsync: activateCardAsync } = useActivatePendingCard();
    const { mutateAsync: creditCardActivation } = useActivatePendingCreditCard();

    const [lastCardTriedAdded, setLastCardTriedAdded] = useState<PendingCardTokenItem | undefined>();
    const [statusArray, setStatusArray] = useState<PendingCardStatus[]>(() => []);

    const setStatusForToken = useCallback(
        (item: PendingCardTokenItem, statusToSet: PendingCardTokenActionResult) => {
            const tokenId = item.source === 'te' ? item.token.eikaTokenId : item.token.id;
            setStatusArray((prev) => {
                return [
                    ...prev.filter((card) => card.tokenId !== tokenId),
                    {
                        tokenId,
                        status: statusToSet,
                    },
                ];
            });
        },
        [setStatusArray],
    );

    const activateCard = useCallback(
        (pendingItem: PendingCardTokenItem) => {
            // (pendingCardId: string) => {
            if (!enabled || !pendingItem) return;
            setLastCardTriedAdded(pendingItem);

            setStatusForToken(pendingItem, 'loading');
            if (pendingItem.source === 'te') {
                activateCardAsync(pendingItem.token)
                    .then(() => {
                        setStatusForToken(pendingItem, 'success');
                        if (shouldInvalidateOnSuccess) debouncedInvalidateFunction();
                    })
                    .catch(() => setStatusForToken(pendingItem, 'error'));
            } else {
                creditCardActivation(pendingItem)
                    .then(() => {
                        setStatusForToken(pendingItem, 'success');
                        if (shouldInvalidateOnSuccess) debouncedInvalidateFunction();
                    })
                    .catch(() => setStatusForToken(pendingItem, 'error'));
            }
        },
        [
            enabled,
            setStatusForToken,
            activateCardAsync,
            shouldInvalidateOnSuccess,
            debouncedInvalidateFunction,
            creditCardActivation,
        ],
    );

    const invalidateAndClear = useCallback(async () => {
        setStatusArray([]);
        await invalidateTokens();
    }, [setStatusArray, invalidateTokens]);

    return {
        activateCard,
        lastCardTriedAdded,
        statusArray,
        hasSuccess: statusArray.some((card) => card.status === 'success'),
        hasLoading: statusArray.some((card) => card.status === 'loading'),
        invalidateAndClear,
    };
};
